// src/pages/maintenance/QuickMaintenanceStatusModal.js
// One-click "put this under maintenance" action — a focused subset of AssetModal.js's status
// fields (status, backdatable statusSince, statusReason), reachable from a single prominent
// button instead of opening the full multi-field Edit form, which buried this well enough to be
// hard to find/rediscover (raised directly by the user — a new tenant especially would have no
// reason to think "Edit" is where you'd go to flag equipment as down for repair). Flipping BACK to
// Operational doesn't need any of these fields, so it's just a plain ConfirmModal at the call site
// instead of a second variant of this component.
import React, { useState } from 'react';
import maintenanceService from '../../services/maintenanceService';
import SearchableSelect from '../../components/SearchableSelect';

const SUPPRESSING_OPTIONS = [
  { value: 'under_maintenance', label: 'Under Maintenance' },
  { value: 'out_of_service', label: 'Out of Service' },
];

// Mirrors AssetModal.js's own helpers exactly — datetime-local inputs give local wall-clock time
// with no timezone info, so round-trip through explicit local Date components on both ends rather
// than trusting a bare string parse (ambiguous once it reaches the server's own timezone).
const isoToDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const datetimeLocalToISO = (value) => {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = (timePart || '00:00').split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm).toISOString();
};

export default function QuickMaintenanceStatusModal({ asset, onSave, onClose }) {
  const [status, setStatus] = useState('under_maintenance');
  const [statusSince, setStatusSince] = useState(isoToDatetimeLocal(new Date().toISOString()));
  const [statusReason, setStatusReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedResult, setSavedResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const result = await maintenanceService.updateAsset(asset.id, {
        status,
        statusSince: datetimeLocalToISO(statusSince),
        statusReason: statusReason.trim() || null,
      });
      // Worth an explicit confirmation rather than silently closing — this count is often the
      // whole point of backdating statusSince (clearing a real backlog of pending alarms).
      if (result.autoAcknowledgedReadingsCount > 0) setSavedResult(result);
      else onSave(result);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update status.');
    } finally {
      setSaving(false);
    }
  };

  if (savedResult) {
    return (
      <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">✓ Asset Updated</h5>
            </div>
            <div className="modal-body text-center py-4">
              <div className="mb-2" style={{ fontSize: 32 }}>✅</div>
              <div>
                <strong>{savedResult.autoAcknowledgedReadingsCount}</strong> pending temperature
                alarm{savedResult.autoAcknowledgedReadingsCount === 1 ? '' : 's'} auto-acknowledged.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => onSave(savedResult)}>Done</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">🔧 Mark "{asset.name}" Under Maintenance</h5>
            <button className="btn-close" onClick={onClose} disabled={saving} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Status</label>
              <SearchableSelect
                options={SUPPRESSING_OPTIONS}
                value={status}
                onChange={v => setStatus(v || 'under_maintenance')}
              />

              <label className="form-label fw-semibold mt-3" style={{ fontSize: 13 }}>
                {SUPPRESSING_OPTIONS.find(o => o.value === status)?.label} since
              </label>
              <input type="datetime-local" className="form-control form-control-sm"
                value={statusSince} onChange={e => setStatusSince(e.target.value)}
                max={isoToDatetimeLocal(new Date().toISOString())} />
              <div className="form-text" style={{ fontSize: 11 }}>
                Backdate this if the equipment was actually taken down earlier than now — pending
                temperature alarms recorded from that time forward will be auto-acknowledged in one
                shot, and new ones auto-acknowledged too, until this is set back to Operational.
              </div>

              <label className="form-label fw-semibold mt-3" style={{ fontSize: 13 }}>Reason (optional)</label>
              <input type="text" className="form-control form-control-sm" value={statusReason}
                onChange={e => setStatusReason(e.target.value)}
                placeholder="e.g. technician requested shutdown to defrost evaporator" />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-warning" disabled={saving}>
                {saving ? 'Saving…' : '🔧 Confirm'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
